
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProfileActionsDropdown } from "./ProfileActionsDropdown";

interface ProfileActionsProps {
  isOwnProfile: boolean;
  onEdit: () => void;
  profileId?: string;
  profile?: any;
}

const ProfileActions = ({ isOwnProfile, onEdit, profileId, profile }: ProfileActionsProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isFollowing, setIsFollowing] = useState(false);
  
  // Check if current user follows this profile
  const { data: followStatus, isLoading: followStatusLoading } = useQuery({
    queryKey: ['follow-status', user?.id, profileId],
    queryFn: async () => {
      if (!user?.id || !profileId || isOwnProfile) return false;
      
      const { data, error } = await supabase
        .from('followers')
        .select('*')
        .eq('follower_id', user.id)
        .eq('following_id', profileId)
        .maybeSingle();
        
      if (error) {
        console.error("Error checking follow status:", error);
        return false;
      }
      
      return !!data;
    },
    enabled: !!user?.id && !!profileId && !isOwnProfile
  });
  
  // Update local state when data changes
  useEffect(() => {
    if (followStatus !== undefined) {
      setIsFollowing(followStatus);
    }
  }, [followStatus]);
  
  // Follow/Unfollow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !profileId) throw new Error("Not authenticated or missing profile ID");
      
      if (isFollowing) {
        // Unfollow - Delete the follow relationship
        const { error } = await supabase
          .from('followers')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', profileId);
          
        if (error) throw error;
        return { action: 'unfollowed' };
      } else {
        // Follow - Create a new follow relationship
        const { error } = await supabase
          .from('followers')
          .insert({
            follower_id: user.id,
            following_id: profileId
          });
          
        if (error) throw error;
        return { action: 'followed' };
      }
    },
    onSuccess: (data) => {
      // Update local state
      setIsFollowing(data.action === 'followed');
      
      // Show success message
      toast.success(data.action === 'followed' ? 'Successfully followed user' : 'Successfully unfollowed user');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['follow-status'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['public-profile'] });
      queryClient.invalidateQueries({ queryKey: ['followers-count'] });
      queryClient.invalidateQueries({ queryKey: ['following-count'] });
    },
    onError: (error) => {
      console.error("Error following/unfollowing:", error);
      toast.error('Failed to update follow status. Please try again.');
    }
  });
  
  const handleFollowClick = () => {
    if (!user) {
      toast.error('Please sign in to follow users');
      return;
    }
    
    followMutation.mutate();
  };

  const currentUrl = window.location.href;

  return (
    <div className="flex gap-3">
      {isOwnProfile ? (
        <button 
          onClick={onEdit}
          className="text-sm text-white/70 hover:text-white transition-colors duration-200 underline underline-offset-2"
        >
          edit profile
        </button>
      ) : (
        <>
          <Button 
            className="flex-1"
            onClick={handleFollowClick}
            disabled={followMutation.isPending || followStatusLoading}
            variant={isFollowing ? "outline" : "default"}
          >
            {followMutation.isPending ? 'Processing...' : isFollowing ? 'Unfollow' : 'Follow'}
          </Button>
          {profile && (
            <ProfileActionsDropdown
              profile={profile}
              currentUrl={currentUrl}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProfileActions;
